/**
 * Shared axios singleton + ApiError class. Used by both brand and admin apps.
 * Auth is unified on Firebase Web SDK (Phase 1.3.1) — interceptor injects
 * Bearer from auth.currentUser.getIdToken() on every request, with single-
 * flight refresh + retry-once on 401.
 *
 * BE error envelope (per Go handbook PREREQUISITES.md):
 *   { "error": { "code": "UPPER_SNAKE_CASE", "message": "..." }, "trace_id": "..." }
 *
 * On any non-2xx response, the response interceptor parses this envelope and
 * throws an ApiError. Consumers either:
 *   1. Let it propagate to React Query, which surfaces it via `error` (typed),
 *      and DataTable's `error: ApiError | null` prop renders a friendly toast.
 *   2. Catch it explicitly to handle specific codes (e.g. INVALID_STATUS).
 *
 * No `?.` fallbacks anywhere — if the BE drops the error envelope, we throw
 * an UNKNOWN ApiError loudly rather than silently rendering a network error.
 */
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { auth } from '../auth/firebaseClient';
import { errorParts, REQUEST_FAILED_MESSAGE } from './errorParts';
import { NO_FIREBASE_USER, refreshFailureKind } from './refreshFailure';
import { runtimeConfig } from '../config/runtimeConfig';

const RAW_BASE = (runtimeConfig('API_URL') ?? 'http://localhost:8080').replace(
  /\/$/,
  '',
);

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public httpStatus: number,
    public traceId?: string,
    /** Optional machine-readable context (RFC 7807-style extension member) —
     *  e.g. CONFLICT + { active_import_id: string }. Present only on the
     *  specific endpoints that populate it; undefined everywhere else. */
    public details?: unknown,
    /** Seconds from the response's Retry-After header (Go's rate limits send it on a 429); undefined when absent. */
    public retryAfterS?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface GoErrorEnvelope {
  error?: { code: string; message: string; details?: unknown };
  trace_id?: string;
}

type RetriableConfig = AxiosRequestConfig & { _retry?: boolean };

export const apiClient = axios.create({
  baseURL: `${RAW_BASE}/api/v1`,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// Inject Bearer from Firebase auth on every request.
apiClient.interceptors.request.use(async (cfg: InternalAxiosRequestConfig) => {
  const u = auth.currentUser;
  if (u) {
    const token = await u.getIdToken();
    cfg.headers.set('Authorization', `Bearer ${token}`);
  }
  return cfg;
});

// Single-flight refresh on 401 — concurrent failed requests share one promise.
let refreshPromise: Promise<string> | null = null;

async function singleFlightRefresh(): Promise<string> {
  const u = auth.currentUser;
  if (!u) throw new Error(NO_FIREBASE_USER);
  if (refreshPromise) return refreshPromise;
  refreshPromise = u.getIdToken(true);
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

apiClient.interceptors.response.use(
  (resp) => resp,
  async (err: AxiosError<GoErrorEnvelope>) => {
    if (!err.response) {
      throw new ApiError(
        'NETWORK_ERROR',
        err.message.length > 0 ? err.message : 'Network error',
        0,
      );
    }

    const { status, data, config } = err.response;
    const cfg = config as RetriableConfig;

    // 401 + not already retried → refresh + retry once
    if (status === 401 && cfg._retry !== true) {
      try {
        const fresh = await singleFlightRefresh();
        const retryConfig: RetriableConfig = {
          ...cfg,
          _retry: true,
          headers: {
            ...(cfg.headers ?? {}),
            Authorization: `Bearer ${fresh}`,
          },
        };
        return apiClient.request(retryConfig);
      } catch (refreshErr) {
        // Only a refresh Firebase refused for the credential ends the session; a refresh that failed on the way
        // (offline, Firebase 5xx) is retryable and must not read as a 401.
        if (refreshFailureKind(refreshErr) === 'network') {
          throw new ApiError('NETWORK_ERROR', "Couldn't refresh the session token", 0, data?.trace_id);
        }
        throw new ApiError(
          'UNAUTHORIZED',
          'Session expired',
          401,
          data?.trace_id,
        );
      }
    }

    const parts = errorParts(data, err.message.length > 0 ? err.message : REQUEST_FAILED_MESSAGE);
    const retryAfter = String(err.response.headers?.['retry-after'] ?? '');
    throw new ApiError(parts.code, parts.message, status, data?.trace_id, parts.details, /^\d+$/.test(retryAfter) ? Number(retryAfter) : undefined);
  },
);
